import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import api from '../utils/api';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.fly8.com';

const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 < Date.now() : false;
  } catch {
    return true;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');

      if (!token || !savedUser) {
        setLoading(false);
        return;
      }

      if (!isTokenExpired(token)) {
        // Access token still valid — restore session immediately, no network call
        setUser(JSON.parse(savedUser));
        setLoading(false);
        return;
      }

      // Access token expired — attempt silent refresh using the HttpOnly cookie
      try {
        const { data } = await axios.post(
          `${API_URL}/api/v1/auth/refresh`,
          {},
          { withCredentials: true }
        );
        localStorage.setItem('token', data.token);
        setUser(JSON.parse(savedUser));
      } catch {
        // Refresh token also expired or absent — require re-login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('student');
      }

      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { token, user: userData } = response.data;
    
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  const signup = async (data) => {
    const response = await api.post('/auth/signup', data);
    const { token, user: userData } = response.data;
    
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  const logout = async () => {
    try {
      // Clear the HttpOnly refresh token cookie on the server
      await api.post('/auth/logout');
    } catch {
      // Proceed with local logout even if the server call fails
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('student');
    setUser(null);
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{ user, login, signup, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};